import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Text,
    Card,
    CardBody,
    SimpleGrid,
    Checkbox,
    Badge,
    useToast,
    Spinner,
    Center,
    Alert,
    AlertIcon,
    Box,
    useColorModeValue,
    Image,
    Divider,
} from '@chakra-ui/react';
import { MdSearch, MdFitnessCenter } from 'react-icons/md';
import Icon from 'components/icons/Icon';
import { exerciseService, Exercise, ExercisesResponse } from 'services/exerciseService';

interface ExerciseSelectorProps {
    isOpen: boolean;
    onClose: () => void;
    onExercisesSelected: (exercises: Exercise[]) => void;
}

const ExerciseSelector: React.FC<ExerciseSelectorProps> = ({
    isOpen,
    onClose,
    onExercisesSelected
}) => {
    const [exercises, setExercises] = useState<Exercise[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedExercises, setSelectedExercises] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [muscleGroupFilter, setMuscleGroupFilter] = useState('');
    const [equipmentFilter, setEquipmentFilter] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const toast = useToast();
    const bgColor = useColorModeValue('white', 'navy.800');
    const borderColor = useColorModeValue('gray.200', 'navy.600');
    const cardBg = useColorModeValue('gray.50', 'navy.700');
    const textColor = useColorModeValue('gray.800', 'white');
    const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
    const inputBg = useColorModeValue('white', 'navy.900');
    const inputBorderColor = useColorModeValue('gray.200', 'navy.500');
    const headerBg = useColorModeValue('gray.50', 'navy.900');
    const selectedCardBg = useColorModeValue('blue.50', 'navy.600');

    useEffect(() => {
        if (isOpen) {
            fetchExercises(true);
        }
    }, [isOpen, searchTerm, categoryFilter, muscleGroupFilter, equipmentFilter]);

    const fetchExercises = async (reset: boolean = false) => {
        setLoading(true);
        setError(null);

        try {
            const currentPage = reset ? 1 : page;
            const response: ExercisesResponse = await exerciseService.getExercises({
                page: currentPage,
                limit: 20,
                search: searchTerm || undefined,
                category: categoryFilter || undefined,
                muscle_group: muscleGroupFilter || undefined,
                equipment: equipmentFilter || undefined,
                public_only: true
            });

            if (reset) {
                setExercises(response.exercises);
                setPage(1);
            } else {
                setExercises(prev => [...prev, ...response.exercises]);
            }

            setHasMore(response.exercises.length === 20);
            if (reset) setPage(2);
            else setPage(prev => prev + 1);

        } catch (error) {
            console.error('Error fetching exercises:', error);
            setError(error instanceof Error ? error.message : 'Failed to load exercises');
            toast({
                title: 'Error',
                description: 'Failed to load exercises',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };

    const handleExerciseToggle = (exerciseId: string) => {
        setSelectedExercises(prev => {
            const newSet = new Set(prev);
            if (newSet.has(exerciseId)) {
                newSet.delete(exerciseId);
            } else {
                newSet.add(exerciseId);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        if (selectedExercises.size === exercises.length) {
            setSelectedExercises(new Set());
        } else {
            setSelectedExercises(new Set(exercises.map(ex => ex.id)));
        }
    };

    const handleConfirm = () => {
        const selected = exercises.filter(ex => selectedExercises.has(ex.id));
        onExercisesSelected(selected);

        // Reset state
        setSelectedExercises(new Set());
        setSearchTerm('');
        setCategoryFilter('');
        setMuscleGroupFilter('');
        setEquipmentFilter('');
        setPage(1);
        setExercises([]);
    };

    const handleCancel = () => {
        setSelectedExercises(new Set());
        setSearchTerm('');
        setCategoryFilter('');
        setMuscleGroupFilter('');
        setEquipmentFilter('');
        setPage(1);
        setExercises([]);
        onClose();
    };

    const filteredExercises = exercises.filter(exercise => {
        const matchesSearch = !searchTerm ||
            exercise.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exercise.description.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesSearch;
    });

    return (
        <Modal isOpen={isOpen} onClose={handleCancel} size="4xl" scrollBehavior="inside">
            <ModalOverlay bg={useColorModeValue('blackAlpha.300', 'blackAlpha.600')} backdropFilter="blur(10px)" />
            <ModalContent 
                maxH="90vh" 
                bg={bgColor}
                borderColor={borderColor}
                border="1px"
                display="flex"
                flexDirection="column"
            >
                <ModalHeader 
                    borderBottom="1px" 
                    borderColor={borderColor}
                    bg={headerBg}
                    borderTopRadius="md"
                >
                    <VStack align="start" spacing={2}>
                        <Text fontSize="xl" fontWeight="bold" color={textColor}>
                            Select Exercises
                        </Text>
                        <HStack spacing={2}>
                            <Badge colorScheme="blue">
                                {selectedExercises.size} selected
                            </Badge>
                            <Badge colorScheme="green">
                                {exercises.length} available
                            </Badge>
                        </HStack>
                    </VStack>
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody flex="1" overflowY="auto">
                    <VStack spacing={4} align="stretch">
                        {/* Search and Filters */}
                        <Card 
                            variant="outline"
                            bg={cardBg}
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <VStack spacing={4} align="stretch">
                                    <InputGroup>
                                        <InputLeftElement pointerEvents="none">
                                            <Icon as={MdSearch} color={mutedTextColor} />
                                        </InputLeftElement>
                                        <Input
                                            placeholder="Search exercises..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            bg={inputBg}
                                            borderColor={inputBorderColor}
                                            color={textColor}
                                            _placeholder={{ color: mutedTextColor }}
                                            _focus={{
                                                borderColor: 'blue.500',
                                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                            }}
                                        />
                                    </InputGroup>

                                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                        <Select
                                            placeholder="Filter by category"
                                            value={categoryFilter}
                                            onChange={(e) => setCategoryFilter(e.target.value)}
                                            bg={inputBg}
                                            borderColor={inputBorderColor}
                                            color={textColor}
                                            _focus={{
                                                borderColor: 'blue.500',
                                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                            }}
                                        >
                                            <option value="strength">Strength</option>
                                            <option value="cardio">Cardio</option>
                                            <option value="flexibility">Flexibility</option>
                                            <option value="balance">Balance</option>
                                            <option value="endurance">Endurance</option>
                                        </Select>

                                        <Select
                                            placeholder="Filter by muscle group"
                                            value={muscleGroupFilter}
                                            onChange={(e) => setMuscleGroupFilter(e.target.value)}
                                            bg={inputBg}
                                            borderColor={inputBorderColor}
                                            color={textColor}
                                            _focus={{
                                                borderColor: 'blue.500',
                                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                            }}
                                        >
                                            <option value="chest">Chest</option>
                                            <option value="back">Back</option>
                                            <option value="shoulders">Shoulders</option>
                                            <option value="arms">Arms</option>
                                            <option value="legs">Legs</option>
                                            <option value="core">Core</option>
                                            <option value="full_body">Full Body</option>
                                        </Select>

                                        <Select
                                            placeholder="Filter by equipment"
                                            value={equipmentFilter}
                                            onChange={(e) => setEquipmentFilter(e.target.value)}
                                            bg={inputBg}
                                            borderColor={inputBorderColor}
                                            color={textColor}
                                            _focus={{
                                                borderColor: 'blue.500',
                                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                            }}
                                        >
                                            <option value="bodyweight">Bodyweight</option>
                                            <option value="dumbbells">Dumbbells</option>
                                            <option value="barbell">Barbell</option>
                                            <option value="machines">Machines</option>
                                            <option value="kettlebells">Kettlebells</option>
                                            <option value="resistance_bands">Resistance Bands</option>
                                            <option value="cables">Cables</option>
                                        </Select>
                                    </SimpleGrid>

                                    <HStack justify="space-between">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={handleSelectAll}
                                        >
                                            {selectedExercises.size === exercises.length ? 'Deselect All' : 'Select All'}
                                        </Button>
                                        <Text fontSize="sm" color={mutedTextColor}>
                                            {filteredExercises.length} exercises shown
                                        </Text>
                                    </HStack>
                                </VStack>
                            </CardBody>
                        </Card>

                        {/* Exercise List */}
                        {error ? (
                            <Alert 
                                status="error"
                                bg={useColorModeValue('red.50', 'red.900')}
                                borderColor={useColorModeValue('red.200', 'red.600')}
                                color={useColorModeValue('red.800', 'red.200')}
                            >
                                <AlertIcon />
                                {error}
                            </Alert>
                        ) : loading && exercises.length === 0 ? (
                            <Center py={10}>
                                <VStack spacing={4}>
                                    <Spinner size="lg" />
                                    <Text color={textColor}>Loading exercises...</Text>
                                </VStack>
                            </Center>
                        ) : filteredExercises.length === 0 ? (
                            <Alert 
                                status="info"
                                bg={useColorModeValue('blue.50', 'navy.900')}
                                borderColor={useColorModeValue('blue.200', 'navy.600')}
                                color={useColorModeValue('blue.800', 'blue.200')}
                            >
                                <AlertIcon />
                                No exercises found matching your criteria.
                            </Alert>
                        ) : (
                            <VStack spacing={3} align="stretch">
                                {filteredExercises.map((exercise) => (
                                    <Card
                                        key={exercise.id}
                                        variant="outline"
                                        cursor="pointer"
                                        _hover={{ 
                                            borderColor: useColorModeValue('blue.300', 'blue.400'),
                                            boxShadow: useColorModeValue('0 2px 8px rgba(0,0,0,0.1)', '0 2px 8px rgba(0,0,0,0.3)')
                                        }}
                                        bg={selectedExercises.has(exercise.id) ? selectedCardBg : cardBg}
                                        borderColor={selectedExercises.has(exercise.id) ? useColorModeValue('blue.300', 'blue.500') : borderColor}
                                        onClick={() => handleExerciseToggle(exercise.id)}
                                    >
                                        <CardBody>
                                            <HStack spacing={4} align="start">
                                                <Checkbox
                                                    isChecked={selectedExercises.has(exercise.id)}
                                                    onChange={() => handleExerciseToggle(exercise.id)}
                                                    size="lg"
                                                />

                                                {exercise.image_url && (
                                                    <Image
                                                        src={exercise.image_url}
                                                        alt={exercise.name}
                                                        boxSize="80px"
                                                        objectFit="cover"
                                                        borderRadius="md"
                                                        fallback={
                                                            <Box
                                                                boxSize="80px"
                                                                bg={useColorModeValue('gray.100', 'navy.600')}
                                                                borderRadius="md"
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="center"
                                                            >
                                                                <Icon as={MdFitnessCenter} color={mutedTextColor} size={6} />
                                                            </Box>
                                                        }
                                                    />
                                                )}

                                                <VStack align="start" spacing={2} flex={1}>
                                                    <VStack align="start" spacing={1}>
                                                        <Text fontWeight="semibold" fontSize="lg" color={textColor}>
                                                            {exercise.name}
                                                        </Text>
                                                        <HStack spacing={2}>
                                                            {exercise.category && (
                                                                <Badge colorScheme="blue" size="sm">
                                                                    {exercise.category}
                                                                </Badge>
                                                            )}
                                                            {exercise.muscle_group && (
                                                                <Badge colorScheme="green" size="sm">
                                                                    {exercise.muscle_group}
                                                                </Badge>
                                                            )}
                                                            {exercise.equipment && (
                                                                <Badge colorScheme="orange" size="sm">
                                                                    {exercise.equipment}
                                                                </Badge>
                                                            )}
                                                        </HStack>
                                                    </VStack>

                                                    {exercise.description && (
                                                        <Text fontSize="sm" color={mutedTextColor} noOfLines={2}>
                                                            {exercise.description}
                                                        </Text>
                                                    )}
                                                </VStack>
                                            </HStack>
                                        </CardBody>
                                    </Card>
                                ))}

                                {/* Load More Button */}
                                {hasMore && !loading && (
                                    <Button
                                        variant="outline"
                                        onClick={() => fetchExercises(false)}
                                        isLoading={loading}
                                    >
                                        Load More Exercises
                                    </Button>
                                )}

                                {loading && exercises.length > 0 && (
                                    <Center py={4}>
                                        <Spinner />
                                    </Center>
                                )}
                            </VStack>
                        )}
                    </VStack>
                </ModalBody>

                <ModalFooter 
                    borderTop="1px" 
                    borderColor={borderColor}
                    bg={headerBg}
                    borderBottomRadius="md"
                >
                    <HStack spacing={3}>
                        <Button variant="outline" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={handleConfirm}
                            isDisabled={selectedExercises.size === 0}
                            leftIcon={<Icon as={MdFitnessCenter} />}
                        >
                            Add {selectedExercises.size} Exercise{selectedExercises.size !== 1 ? 's' : ''}
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ExerciseSelector;
